import { AddTagRequest, CreateImageRequest, CreatePointRequest, CreatePointResponse, CreatePointsResponse, GeocodeType, GetPointResponse, Image, PlaceDetails, PointListingSyncResponse, TelemetryEntry, WebError, circle, viewport } from '../../types';
import { buildQueryString, del, emptyPost, get, logWithAmplitude, memoizeAsync, post, put, throwHttpError } from './api';
import { delay, isDefined, transformLatLng } from '../utils';

import { Base64, SHA256 } from 'crypto-es';
import { getMainPage } from '../page-name';
import { iterateAICount } from './account-api';
import topicData from '../state/topic-data';

export async function getPoint(amplitude_callsite: string, point_id: string, point_key?: string) {
    const queryString = buildQueryString({ point_key });
    const response = await fetch(`/api/points/${point_id}${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the point.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Point', {
        point_id,
        point_key,
    });
    return await (response.json() as Promise<GetPointResponse>);
}

export async function getPointListingSync(amplitude_callsite: string, organization_id: string, point_id: string) {
    const response = await fetch(`/api/directory_entries/${organization_id}/points/${point_id}/listing_sync`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the listing info for the point.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Point Listing Sync', {
        organization_id,
        point_id,
    });
    return await (response.json() as Promise<PointListingSyncResponse>);
}

export async function getPoints(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string,
    created_since?: Date,
    use_cache?: boolean,
    minimal?: boolean,
    limit?: number,
    offset?: number,
    point_id?: string,
    point_key?: string,
) {
    const created_since_string = created_since?.toISOString();
    let queryString = buildQueryString({
        topic_key,
        created_since: created_since_string,
        use_cache,
        minimal,
        limit,
        offset,
        point_id,
        point_key,
    });
    if (topicData.challengeHappeningNow) {
        queryString += `&activated_challenge_id=${topicData.activatedChallengeId ?? ''}`;
        const response = await fetch(`/api/topics/${topic_id}/points/challenge/${topicData.challengeHappeningNow._id.$oid}${queryString}`, get());

        if (!response.ok) {
            await throwHttpError('An error occurred while getting the challenge points.', response);
        }
        return await (response.json() as Promise<GetPointResponse[]>);
    } else {
        const response = await fetch(`/api/topics/${topic_id}/points${queryString}`, get());
        if (!response.ok) {
            await throwHttpError('An error occurred while getting the points.', response);
        }
        return await (response.json() as Promise<GetPointResponse[]>);
    }
}

/**
 * Fetches all points for a topic using pagination to reduce payload size per request.
 * This function automatically handles pagination by fetching points in batches and combining them.
 * 
 * @param amplitude_callsite - The callsite for amplitude tracking
 * @param topic_id - The topic ID to fetch points for
 * @param topic_key - Optional topic key for authentication
 * @param created_since - Optional date filter to get points created after this date
 * @param use_cache - Whether to use cached data (default: true)
 * @param minimal - Whether to fetch minimal point data (default: false)
 * @param page_size - Number of points to fetch per request (default: 500)
 * @returns Promise resolving to array of all points
 */
export async function getAllPointsPaginated(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string,
    created_since?: Date,
    use_cache?: boolean,
    minimal?: boolean,
    page_size: number = 500,
    point_id?: string,
    point_key?: string,
): Promise<GetPointResponse[]> {
    const allPoints: GetPointResponse[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
        const batch = await getPoints(
            amplitude_callsite,
            topic_id,
            topic_key,
            created_since,
            use_cache,
            minimal,
            page_size,
            offset,
            point_id,
            point_key,
        );

        allPoints.push(...batch);

        // If we got fewer points than the page size, we've reached the end
        if (batch.length < page_size) {
            hasMore = false;
        } else {
            offset += page_size;
        }
    }

    return allPoints;
}

export async function getVisitedPoints(
    amplitude_callsite: string,
    topic_ids: string[],
) {
    const topic_ids_string = topic_ids.join(',');
    const url = `/api/points/visited?topic_ids=${topic_ids_string}`;

    const response = await fetch(url, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the visited points.', response);
    }

    return await (response.json() as Promise<GetPointResponse[]>);
}

export async function updatePoint(
    amplitude_callsite: string,
    topic_id: string,
    point: CreatePointRequest,
    point_key?: string,
    geocode_type?: GeocodeType,
    additional_logged_parameters: Object = {}
) {
    if (isDefined(point._id)) {
        const queryString = buildQueryString({ point_key, geocode_type });
        const response = await fetch(`/api/points/${point._id?.$oid}${queryString}`, put(point));
            if (!response.ok) {
                const errorText = await response.text();
                let serverMessage = '';

                try {
                    const webError: WebError = JSON.parse(errorText);
                    serverMessage = webError.message;
                } catch (e) {
                    console.error(`Failed to parse error text: ${errorText}`);
                }

                // Check if the server message is in the specific format
                if (serverMessage.startsWith('The point could not be validated:')) {
                    // Pass on the server message to the user
                    getMainPage().errorToastElement.showToastAndThrow(`Try Refreshing your page and trying again: ${serverMessage}`);
                }

                await throwHttpError('An error occurred while updating the point.', response);
            }

        logWithAmplitude(
            amplitude_callsite,
            'Point Updated',
            Object.assign(
                {},
                {
                    topic_id,
                    point_id: point._id.$oid,
                    point_key,
                },
                additional_logged_parameters
            )
        );
    } else {
        throw new Error('Point passed into edit has no id.');
    }
}

export async function approvePoint(
    amplitude_callsite: string,
    point_id: string,
    point_key?: string,
) {
    const queryString = buildQueryString({ point_key });
    const response = await fetch(`/api/points/${point_id}/approve${queryString}`, emptyPost());

    if (!response.ok) {
        await throwHttpError('An error occurred while approving the point.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Point Approved', { point_id });
}

export async function editPointUpvote(
    amplitude_callsite: string,
    topic_id: string,
    point_id: string,
    topic_key?: string
) {
    const response = await fetch(`/api/points/${point_id}/likes`, emptyPost());

    if (!response.ok) {
        if (response.status === 400) {
            getMainPage().errorToastElement.showToastAndThrow('Our upvoting/liking feature uses cookies, and it looks like yours are disabled.');
        }
        await throwHttpError('An error occurred while upvoting the point.', response);
    }

    logWithAmplitude(amplitude_callsite, `Changed Upvote on Point`, {
        topic_id,
        topic_key,
        point_id,
    });
}


export async function resendPointEmail(
    amplitude_callsite: string,
    point_id: string,
    point_key?: string
) {
    const queryString = buildQueryString({ point_key });
    const response = await fetch(`/api/points/${point_id}/resend_email${queryString}`, post({}));

    if (!response.ok) {
        await throwHttpError('An error occurred while resending the point email.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Resend Point Email', {
        point_id,
        point_key,
    });
}


export async function addPointImage(
    amplitude_callsite: string,
    topic_id: string,
    point_id: string,
    image: CreateImageRequest,
    point_key?: string,
    orientation?: number
) {
    const queryString = buildQueryString({ point_key, orientation });
    const response = await fetch(`/api/points/${point_id}/images${queryString}`, post(image));
    if (!response.ok) {
        await throwHttpError(
            `An error occurred while adding an image (${image.name}) to the point.`,
            response
        );
    }

    const createdImage = (await response.json()) as Image;

    // Dev hack: rewrite Azure blob URLs for local Docker development. Skip CDN URLs.
    if (window.location.hostname === 'localhost' && createdImage.sas_url.includes('.blob.core.windows.net')) {
        createdImage.sas_url = createdImage.sas_url.replace('storage', 'localhost');
    }

    logWithAmplitude(amplitude_callsite, 'Point Images Added', {
        topic_id,
        point_id,
        point_key,
    });

    return createdImage;
}

export async function addPointImageFromUrl(
    amplitude_callsite: string,
    topic_id: string,
    point_id: string,
    url: string,
    name: string,
    point_key?: string
) {
    const queryString = buildQueryString({ point_key });
    const body = {
        url,
        name,
    };
    const response = await fetch(`/api/points/${point_id}/image_from_url${queryString}`, post(body));
    if (!response.ok) {
        await throwHttpError(
            `An error occurred while adding an image from URL to the point.`,
            response
        );
    }

    const createdImage = (await response.json()) as Image;

    logWithAmplitude(amplitude_callsite, 'Point Images Added From URL', {
        topic_id,
        point_id,
        point_key,
    });

    return createdImage;
}

// Memoizes an async function, using the first parameter as the cache key.
// use_cache must be the last variable provided in the list of arguments, so this is separated into a different function
function memoizeGetPointImagesAsync(f: Function, keys_to_use?: number[]) {
    const cache = {} as any;

    const memoized = async (...args: any[]) => {
        let hashKey: any[] = [];
        if (keys_to_use !== undefined) {
            for (let i = 0; i < keys_to_use.length; i++) {
                hashKey.push(args[keys_to_use[i]]);
            }
        } else {
            hashKey = [...args];
        }
        const hash = SHA256(hashKey.toString());
        const hashedString = hash.toString(Base64).substring(0, 10);
        const useCache = args[args.length - 1] !== false;
        if (useCache && hashedString in cache) {
            return cache[hashedString];
        } else {
            const result = await f.apply(undefined, args);
            cache[hashedString] = result;
            return result;
        }
    };

    // Expose the cache object
    (memoized as any).cache = cache;

    return memoized;
}

// use_cache must be the last variable in order for memoizeGetPointImagesAsync to work correctly
export const getPointImages = memoizeGetPointImagesAsync(
    async (
        amplitude_callsite: string,
        topic_id: string,
        point_id: string,
        point_key?: string,
        use_cache: boolean = true /* add new variables before this variable */
    ) => {
        // if point_key looks like an email, dont use cache
        // this is a little hacky to override cases where the user just accessed the map with an email gate
        // in general that whole process should be rethought
        if (point_key?.includes('@') === true) {
            use_cache = false;
        }
        const queryString = buildQueryString({
            point_key,
            use_cache,
        });
        const response = await fetch(`/api/points/${point_id}/images${queryString}`, get());

        if (!response.ok) {
            await throwHttpError('An error occurred while getting the images.', response);
        }

        const images = (await response.json()) as Image[];

        // Dev hack: rewrite Azure blob URLs for local Docker development. Skip CDN URLs.
        if (window.location.hostname === 'localhost') {
            for (let i = 0; i < images.length; i++) {
                if (images[i].sas_url.includes('.blob.core.windows.net')) {
                    images[i].sas_url = images[i].sas_url.replace('storage', 'localhost');
                }
            }
        }

        logWithAmplitude(amplitude_callsite, 'Get Images', {
            topic_id,
            point_id,
            point_key,
        });
        return images;
    },
    [1, 2]
);

// Create a cache invalidation function
export function invalidatePointImagesCache(topic_id: string, point_id: string) {
    try {
        // Create the same hash that would be used by the cache function
        const hashKey = [topic_id, point_id];
        const hash = SHA256(hashKey.toString());
        const hashedString = hash.toString(Base64).substring(0, 10);

        // Access the internal cache object more safely
        const cache = (getPointImages as any).cache;
        if (isDefined(cache) && hashedString in cache) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete cache[hashedString];
            return true; // Successful deletion
        }
        return false; // Nothing to delete
    } catch (e) {
        console.error('Error invalidating point images cache:', e);
        return false; // Error during deletion
    }
}

export async function getTopicImages(
    topic_id: string,
    amplitude_callsite: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({
        topic_key,
    });
    const response = await fetch(`/api/topics/${topic_id}/images${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the images.', response);
    }

    const images = (await response.json()) as Image[];

    // Dev hack: rewrite Azure blob URLs for local Docker development. Skip CDN URLs.
    if (window.location.hostname === 'localhost') {
        for (let i = 0; i < images.length; i++) {
            if (images[i].sas_url.includes('.blob.core.windows.net')) {
                images[i].sas_url = images[i].sas_url.replace('storage', 'localhost');
            }
        }
    }

    logWithAmplitude(amplitude_callsite, 'Get Images', {
        topic_id,
    });
    return images;
    }


export async function deletePointImage(
    amplitude_callsite: string,
    topic_id: string,
    point_id: string,
    image_id: string,
    point_key?: string
) {
    const queryString = buildQueryString({ point_key });
    const response = await fetch(`/api/points/${point_id}/images/${image_id}${queryString}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the point image.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Point Image Deleted', {
        topic_id,
        point_id,
        image_id,
    });
}

export async function deletePoint(
    amplitude_callsite: string,
    topic_id: string,
    point_id: string,
    point_key?: string,
) {
    const queryString = buildQueryString({ point_key });
    const response = await fetch(`/api/points/${point_id}${queryString}`, del());
    if (!response.ok) {
        getMainPage().errorToastElement.showToast('An error occurred while deleting the point.');
        await throwHttpError('An error occurred while deleting the point.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Point Deleted', {
        topic_id,
        point_id,
        point_key,
    });
}

export const getPointPlaceDetails = memoizeAsync(
    async (amplitude_callsite: string, topic_id: string, point_id: string, point_key?: string) => {
        const queryString = buildQueryString({ point_key });
        const response = await fetch(`/api/points/${point_id}/place_details${queryString}`, get());

        if (!response.ok) {
            await throwHttpError('An error occurred while getting the place details.', response);
        }

        logWithAmplitude(amplitude_callsite, 'Get Point Place Details', {
            topic_id,
            point_id,
            point_key, // can be topic key for not logged in
        });
        const place_details = (await response.json()) as PlaceDetails;
        return place_details;
    }
);

export async function addPoint(
    amplitude_callsite: string,
    topic_id: string,
    point: CreatePointRequest,
    topic_key?: string,
    geocode_type?: GeocodeType,
) {
    const point_response = await addPointInner(topic_id, point, topic_key, geocode_type);
    logWithAmplitude(amplitude_callsite, 'Point Added', {
        topic_id,
        topic_key,
        point_id: point_response._id.$oid,
    });
    return point_response;
}

async function addPointInner(
    topic_id: string,
    point: CreatePointRequest,
    topic_key?: string,
    geocode_type?: GeocodeType,
) {
    const queryString = buildQueryString({ topic_key, geocode_type });
    const response = await fetch(`/api/topics/${topic_id}/points${queryString}`, post(point));
    if (!response.ok) {
        getMainPage().errorToastElement.showToast('An error occurred while creating the point.');
        await throwHttpError('An error occurred while creating the point.', response);
    }

    return await (response.json() as Promise<CreatePointResponse>);
}

export async function addPoints(
    amplitude_callsite: string,
    topic_id: string,
    points: CreatePointRequest[],
    topic_key?: string,
    geocode_type?: GeocodeType,
) {

    const CHUNK_SIZE = 75;
    const chunks = Math.ceil(points.length / CHUNK_SIZE);
    const createTaskResult: CreatePointsResponse = {
        failed_geocodes: [],
        failed_validations: [],
        updated_point_ids: [],
    };

    const queryString = buildQueryString({ topic_key, geocode_type });

    for (let i = 0; i < chunks; i++) {
        const chunk = points.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const response = await fetch(`/api/topics/${topic_id}/points/bulk${queryString}`, post(chunk));

        if (!response.ok) {
            await throwHttpError('An error occurred while creating the points.', response);
        }

        const result = await (response.json() as Promise<CreatePointsResponse>);
        createTaskResult.failed_geocodes.push(...result.failed_geocodes);
        createTaskResult.failed_validations.push(...result.failed_validations);
        createTaskResult.updated_point_ids.push(...result.updated_point_ids);

        // Add delay between chunks if not the last chunk
        if (i < chunks - 1) {
            await delay(20);
        }
    }

    logWithAmplitude(amplitude_callsite, 'Points Added', {
        topic_id,
        topic_key,
        points_added: points.length,
    });
    return createTaskResult;
}

export async function updatePoints(
    amplitude_callsite: string,
    topic_id: string,
    points: CreatePointRequest[],
    topic_key?: string,
    geocode_type?: GeocodeType,
) {
    const CHUNK_SIZE = 75;
    const chunks = Math.ceil(points.length / CHUNK_SIZE);
    const updateTaskResult: CreatePointsResponse = {
        failed_geocodes: [],
        failed_validations: [],
        updated_point_ids: [],
    };

    for (let i = 0; i < chunks; i++) {
        const chunk = points.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const queryString = buildQueryString({ topic_key, geocode_type });
        const response = await fetch(`/api/topics/${topic_id}/points/bulk${queryString}`, put(chunk));
        if (!response.ok) {
            await throwHttpError('An error occurred while creating the points.', response);
        }

        const result = await (response.json() as Promise<CreatePointsResponse>);
        updateTaskResult.failed_geocodes.push(...result.failed_geocodes);
        updateTaskResult.failed_validations.push(...result.failed_validations);
        updateTaskResult.updated_point_ids.push(...result.updated_point_ids);

        // Add delay between chunks if not the last chunk
        if (i < chunks - 1) {
            await delay(20);
        }
    }

    logWithAmplitude(amplitude_callsite, 'Points Updated', {
        topic_id,
        topic_key,
        points_updated: points.length,
    });

    return updateTaskResult;
}

export async function getPointViews(
    amplitude_callsite: string,
    topic_id: string,
    point_id: string,
    point_key?: string
) {
    const queryString = buildQueryString({ point_key });
    const response = await fetch(`/api/points/${point_id}/views${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the point views.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Point Views', {
        topic_id,
        point_id,
        point_key,
    });
    return await (response.json() as Promise<TelemetryEntry[]>);
}

export function pointRequestFromPoint(p: GetPointResponse): CreatePointRequest {
    const tag_info: AddTagRequest = {
        user_tags: p.tag_info?.user_tags
            .filter(tag => isDefined(tag._id))
            .map(tag => ({ _id: tag._id  }))
            ?? []
    };

    return {
        _id: p._id,
        email: p.email,
        topic_id: p.topic_id,
        topic_category_id: p.topic_category_id,
        location: p.location,
        name: p.name,
        description: p.description,
        special_offer: p.special_offer,
        embedded_video: p.embedded_video,
        custom_icon_url: p.custom_icon_url,
        url: p.url,
        active: p.active,
        featured: p.featured,
        start: p.start,
        end: p.end,
        number: p.number,
        source: p.source,
        tag_info,
        is_gate_hidden: p.is_gate_hidden,
        custom_information: p.custom_information,
        custom_shape: p.custom_shape,
    };
}

// Find nearby places with our api /nearby
export async function find_nearby_places(
    amplitude_callsite: string,
    num_results: number,
    search_circle?: circle,
    search_viewport?: viewport,
    keyword?: string,
    types?: string[],
    iterate_usage: boolean = true
): Promise<CreatePointRequest[]> {
    // check that there is either a search_circle or a search_viewport and not both
    if (search_circle && search_viewport) {
        throw new Error('Cannot have both a search_circle and a search_viewport');
    } else if (!search_circle && !search_viewport) {
        throw new Error('Must have either a search_circle or a search_viewport');
    }

    let search_area: submission_circle | submission_rectangle;
    if (search_circle) {
        search_area = {
            circle: {
                center: transformLatLng(search_circle?.center),
                radius: search_circle?.radius
            }
        };
    } else if (search_viewport) {
        search_area = {
            rectangle: {
                high: transformLatLng(search_viewport.high),
                low: transformLatLng(search_viewport.low)
            }
        };
    } else {
        getMainPage().errorToastElement.showToastAndThrow('Search area not found.');
        return [];
    }

    const body = {
        num_results,
        search_area,
        keyword,
        types
    };
    const topic_id = topicData.topic?._id.$oid;

    if (!topic_id) {
        getMainPage().errorToastElement.showToastAndThrow('Topic not found.');
    }

    const response = await fetch(`/api/nearby/${topic_id ?? ''}?topic_key=${topicData.topicKey ?? ''}`, post(body));
    if (!response.ok) {
        await throwHttpError('An error occurred while finding nearby places.', response);
    }

    const data = await response.json();

    // remove places that appear to be duplicates
    const seen = new Set();
    const unique_data = data.filter((place: CreatePointRequest) => {
        const key = place.location.place_id;
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });



    logWithAmplitude(amplitude_callsite, 'Nearby Places Found', {
        num_results,
        keyword,
    });

    if (iterate_usage && data.length > 0) {
        try {
            await iterateAICount(amplitude_callsite);
        } catch (error) {
            console.error('Exceeded AI usage limit');
        }
    }

    return unique_data;
}

// Onboarding Suggest points with nearby places
// Given a center and some category names, iterate through the categories and find nearby places
export async function suggestPointsOnboarding(
    amplitude_callsite: string,
    search_viewport: viewport,
    category_names: string[],
): Promise<CreatePointRequest[]> {
    const promises = category_names.map(async (category_name) => {
        const num_results = Math.round(18 / category_names.length);
        return await find_nearby_places(
            amplitude_callsite,
            num_results,
            undefined,
            search_viewport,
            category_name,
            undefined,
            false
        );
    });

    const results = await Promise.all(promises);

    const suggested_points = results.flat();

    return suggested_points;
}

interface submission_circle {
    circle : {
        center: { latitude: number; longitude: number };
        radius: number;
    }
}
interface submission_rectangle {
    rectangle: {
        high: { latitude: number; longitude: number };
        low: { latitude: number; longitude: number };
    }
}


